import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import logosvg from "../../../images/logo.svg";
import { forgotPassword, verifyForgotOtp } from "../../../environment";
import useApi from "../../../core/customhook/useApi";
import Swal from "sweetalert2";

const ForgotPassword = () => {
  const routes = all_routes;
  const navigation = useNavigate();

  const [email, setEmail] = useState("");

  const [verifyEmail, setVerifyEmail] = useState("");
  const [verifyOtp, setVerifyOtp] = useState("");

  // FIX: `authStep` was referenced in the JSX (`authStep === "forgotPassword"`,
  // `authStep === "otp"`) but never declared anywhere — this alone would
  // throw "authStep is not defined" and crash the component on render.
  // This now drives a full screen switch between the two steps, same
  // pattern as the login/OTP-verify flow — no modal/overlay.
  const [authStep, setAuthStep] = useState("forgotPassword");

  const forgotPasswordAPI = useApi<any>(forgotPassword, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const verifyForgotOtpAPI = useApi<any>(verifyForgotOtp, {
    method: "POST",
    autoCall: false,
    handleGlobalError: false,
  });

  const handleForgotPassword = async (
    e: React.MouseEvent<HTMLButtonElement>,
  ) => {
    e.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!email.trim()) {
      Swal.fire({
        icon: "error",
        title: "Validation Error",
        text: "Please enter your email address",
      });
      return;
    }

    if (!emailRegex.test(email)) {
      Swal.fire({
        icon: "error",
        title: "Invalid Email",
        text: "Please enter a valid email address",
      });
      return;
    }

    try {
      const payload = {
        email: email.trim(),
      };

      const response = await forgotPasswordAPI.callApi(payload);

      if (response?.success) {
        setVerifyEmail(email.trim());
        setAuthStep("otp");
      } else {
        Swal.fire({
          icon: "error",
          title: "Reset password Failed",
          text: response?.error || "Something went wrong",
        });
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Candidate not found",
      });
    }
  };

  const handleVerifyOTP = async () => {
    try {
      if (!verifyEmail) {
        Swal.fire({
          icon: "warning",
          title: "Email Required",
          text: "Please enter your email address",
        });
        return;
      }

      if (!verifyOtp) {
        Swal.fire({
          icon: "warning",
          title: "OTP Required",
          text: "Please enter OTP",
        });
        return;
      }

      const payload = {
        email: verifyEmail,
        otp: verifyOtp,
      };

      const response = await verifyForgotOtpAPI.callApi(payload);

      if (response?.success) {
        navigation(routes.resetPassword, {
          state: {
            resetToken: response?.data?.resetToken,
          },
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "Verification Failed",
          text: response?.error || "Something went wrong",
        });
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Verification Failed",
        text:
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong",
      });
    }
  };

  const forgotPasswordLoading = forgotPasswordAPI.loading;
  const verifyOTPLoading = verifyForgotOtpAPI.loading;

  return (
    <>
      <div className="container-fluid p-0">
        <div className="row g-0 vh-100">
          {/* RIGHT SIDE - CONTENT + SCROLLING IMAGES */}
          <div className="col-lg-7 d-none d-lg-block">
            <div
              className="vh-100 position-relative overflow-hidden"
              style={{
                background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
              }}
            >
              {/* CONTENT */}
              <div
                className="position-absolute top-50 start-50 translate-middle text-center text-white px-2"
                style={{ zIndex: 2 }}
              >
                <img
                  src={logosvg}
                  alt="logo"
                  className="img-fluid mb-4"
                  style={{ maxWidth: "220px" }}
                />

                <h1
                  className="fw-bold mb-4"
                  style={{ fontSize: "28px", color: "white" }}
                >
                  Welcome to the Aupertz Portal
                </h1>

                <p
                  className="text-light"
                  style={{ fontSize: "18px", lineHeight: "32px" }}
                >
                  Sign in to access your background verification application,
                  upload required documents, track verification progress, and
                  complete your onboarding seamlessly.
                </p>
              </div>

              {/* SCROLLING IMAGES */}
              <div className="scroll-images">
                <img
                  src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d"
                  alt=""
                />
                <img
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978"
                  alt=""
                />
                <img
                  src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3"
                  alt=""
                />
                <img
                  src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f"
                  alt=""
                />
              </div>
            </div>
          </div>

          <div className="col-lg-5 col-md-12">
            <div className="d-flex align-items-center justify-content-center vh-100 bg-white px-4">
              {authStep === "forgotPassword" && (
                <div style={{ width: "100%", maxWidth: "420px" }}>
                  <div className="mx-auto mb-3 text-center">
                    <img
                      src={logosvg}
                      className="img-fluid"
                      alt="Logo"
                      style={{ maxWidth: "170px" }}
                    />
                  </div>

                  <div className="forgot-card">
                    <form>
                      <div className="text-center mb-4">
                        <h2 className="forgot-title mb-2">Forgot Password?</h2>
                        <p className="forgot-subtext mb-0">
                          No worries — enter your email and we'll send you a
                          code to reset your password.
                        </p>
                      </div>

                      <div className="mb-3">
                        <label className="form-label fw-medium">
                          Email Address{" "}
                          <span className="text-danger ms-1">*</span>
                        </label>
                        <div className="input-group input-group-custom">
                          <input
                            type="email"
                            className="form-control"
                            placeholder="Enter your email address"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                          />
                          <span className="input-group-text border-start-0">
                            <i className="ti ti-mail" />
                          </span>
                        </div>
                      </div>

                      <div className="mb-3">
                        <button
                          type="button"
                          onClick={handleForgotPassword}
                          className="btn btn-primary w-100 forgot-submit-btn"
                          disabled={forgotPasswordLoading}
                        >
                          {forgotPasswordLoading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              />
                              Please Wait...
                            </>
                          ) : (
                            "Send Reset Code"
                          )}
                        </button>
                      </div>

                      <div className="text-center">
                        <span className="return-text">
                          Return to{" "}
                          <Link to={all_routes.login} className="text-danger">
                            Sign In
                          </Link>
                        </span>
                      </div>
                    </form>
                  </div>
                </div>
              )}

              {authStep === "otp" && (
                <div style={{ width: "100%", maxWidth: "420px" }}>
                  <div className="mx-auto mb-3 text-center">
                    <img
                      src={logosvg}
                      className="img-fluid"
                      alt="Logo"
                      style={{ maxWidth: "170px" }}
                    />
                  </div>

                  <div className="forgot-card">
                    <div className="text-center mb-4">
                      <h2 className="forgot-title mb-2">OTP Verification</h2>
                      <p className="forgot-subtext mb-0">
                        Enter your registered email address and the OTP sent to
                        your inbox to continue resetting your password.
                      </p>
                    </div>

                    <div className="mb-3">
                      <label className="form-label fw-medium">
                        Email Address{" "}
                        <span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        placeholder="Enter your email address"
                        value={verifyEmail}
                        onChange={(e) => setVerifyEmail(e.target.value)}
                      />
                    </div>

                    <div className="mb-4">
                      <label className="form-label fw-medium">
                        OTP <span className="text-danger ms-1">*</span>
                      </label>
                      <input
                        type="text"
                        maxLength={6}
                        className="form-control otp-modal-input"
                        placeholder="Enter 6-digit OTP"
                        value={verifyOtp}
                        onChange={(e) => setVerifyOtp(e.target.value)}
                      />
                    </div>

                    <div className="mb-3">
                      <button
                        type="button"
                        className="btn btn-primary w-100 forgot-submit-btn"
                        onClick={handleVerifyOTP}
                        disabled={verifyOTPLoading}
                      >
                        {verifyOTPLoading ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                            />
                            Verifying OTP...
                          </>
                        ) : (
                          "Verify OTP"
                        )}
                      </button>
                    </div>

                    <div className="text-center mb-3">
                      <small className="text-muted">
                        Need help? Contact{" "}
                        <a
                          href="mailto:candidatesupport@aupertz.co.in"
                          className="text-danger text-decoration-none"
                        >
                          candidatesupport@aupertz.co.in
                        </a>
                      </small>
                    </div>

                    {/* Back to forgot password */}
                    <div className="text-center">
                      <button
                        type="button"
                        className="btn btn-outline-primary"
                        onClick={() => setAuthStep("forgotPassword")}
                      >
                        ← Back
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ForgotPassword;
